interface Memoization1<S1, T> {
  lastArg1: S1;
  lastResult: T;
}

interface Memoization2<S1, S2, T> {
  lastArg1: S1;
  lastArg2: S2;
  lastResult: T;
}

interface Memoization3<S1, S2, S3, T> {
  lastArg1: S1;
  lastArg2: S2;
  lastArg3: S3;
  lastResult: T;
}

interface Memoization4<S1, S2, S3, S4, T> {
  lastArg1: S1;
  lastArg2: S2;
  lastArg3: S3;
  lastArg4: S4;
  lastResult: T;
}

interface Memoization5<S1, S2, S3, S4, S5, T> {
  lastArg1: S1;
  lastArg2: S2;
  lastArg3: S3;
  lastArg4: S4;
  lastArg5: S5;
  lastResult: T;
}

export function memoize1<S1, T>(f: (arg1: S1) => T): (arg1: S1) => T {
  let memoization: Memoization1<S1, T> | null = null;

  return (arg1: S1) => {
    // Check if we are calling the function again with the same argument
    // as last time
    if (memoization !== null) {
      if (arg1 === memoization.lastArg1) {
        return memoization.lastResult;
      }
    }

    // The argument is different: we must calulate a new result (and save
    // it for next time!)
    const result = f(arg1);
    memoization = {
      lastArg1: arg1,
      lastResult: result,
    };
    return result;
  };
}

export function memoize2<S1, S2, T>(f: (arg1: S1, arg2: S2) => T): (arg1: S1, arg2: S2) => T {
  let memoization: Memoization2<S1, S2, T> | null = null;

  return (arg1: S1, arg2: S2) => {
    // Check if we are calling the function again with the same argument
    // as last time
    if (memoization !== null) {
      if (arg1 === memoization.lastArg1 && arg2 === memoization.lastArg2) {
        return memoization.lastResult;
      }
    }

    // The argument is different: we must calulate a new result (and save
    // it for next time!)
    const result = f(arg1, arg2);
    memoization = {
      lastArg1: arg1,
      lastArg2: arg2,
      lastResult: result,
    };
    return result;
  };
}

export function memoize3<S1, S2, S3, T>(
  f: (arg1: S1, arg2: S2, arg3: S3) => T
): (arg1: S1, arg2: S2, arg3: S3) => T {
  let memoization: Memoization3<S1, S2, S3, T> | null = null;

  return (arg1: S1, arg2: S2, arg3: S3) => {
    // Check if we are calling the function again with the same argument
    // as last time
    if (memoization !== null) {
      if (
        arg1 === memoization.lastArg1 &&
        arg2 === memoization.lastArg2 &&
        arg3 === memoization.lastArg3
      ) {
        return memoization.lastResult;
      }
    }

    // The argument is different: we must calulate a new result (and save
    // it for next time!)
    const result = f(arg1, arg2, arg3);
    memoization = {
      lastArg1: arg1,
      lastArg2: arg2,
      lastArg3: arg3,
      lastResult: result,
    };
    return result;
  };
}

export function memoize4<S1, S2, S3, S4, T>(
  f: (arg1: S1, arg2: S2, arg3: S3, arg4: S4) => T
): (arg1: S1, arg2: S2, arg3: S3, arg4: S4) => T {
  let memoization: Memoization4<S1, S2, S3, S4, T> | null = null;

  return (arg1: S1, arg2: S2, arg3: S3, arg4: S4) => {
    // Check if we are calling the function again with the same argument
    // as last time
    if (memoization !== null) {
      if (
        arg1 === memoization.lastArg1 &&
        arg2 === memoization.lastArg2 &&
        arg3 === memoization.lastArg3 &&
        arg4 === memoization.lastArg4
      ) {
        return memoization.lastResult;
      }
    }

    // The argument is different: we must calulate a new result (and save
    // it for next time!)
    const result = f(arg1, arg2, arg3, arg4);
    memoization = {
      lastArg1: arg1,
      lastArg2: arg2,
      lastArg3: arg3,
      lastArg4: arg4,
      lastResult: result,
    };
    return result;
  };
}

export function memoize5<S1, S2, S3, S4, S5, T>(
  f: (arg1: S1, arg2: S2, arg3: S3, arg4: S4, arg5: S5) => T
): (arg1: S1, arg2: S2, arg3: S3, arg4: S4, arg5: S5) => T {
  let memoization: Memoization5<S1, S2, S3, S4, S5, T> | null = null;

  return (arg1: S1, arg2: S2, arg3: S3, arg4: S4, arg5: S5) => {
    // Check if we are calling the function again with the same argument
    // as last time
    if (memoization !== null) {
      if (
        arg1 === memoization.lastArg1 &&
        arg2 === memoization.lastArg2 &&
        arg3 === memoization.lastArg3 &&
        arg4 === memoization.lastArg4 &&
        arg5 === memoization.lastArg5
      ) {
        return memoization.lastResult;
      }
    }

    // The argument is different: we must calulate a new result (and save
    // it for next time!)
    const result = f(arg1, arg2, arg3, arg4, arg5);
    memoization = {
      lastArg1: arg1,
      lastArg2: arg2,
      lastArg3: arg3,
      lastArg4: arg4,
      lastArg5: arg5,
      lastResult: result,
    };
    return result;
  };
}
